// Lib
import Immutable from 'immutable';

// Utils
import * as rectLib from '../../common/maths/geometry/rect';
import { getDomElementId } from '../element/utils/elementUtil';
import { getX, getY } from '../../common/maths/geometry/dimensionUtil';
import { getElementId } from '../../common/elements/utils/elementPropertyUtils';
import { isBoardLike, isLine } from '../../common/elements/utils/elementTypeUtils';
import { convertClientRectToUnscaledSectionCoordinates } from './utils/canvasPositionUtils';

// Constants
import { CANVAS_VIEWPORT_ID } from './utils/canvasConstants';

// Types
import { MNElement } from '../../common/elements/elementModelTypes';
import { Rect } from '../../common/maths/geometry/rect/rectTypes';
import { Point } from '../../common/maths/geometry/pointTypes';

const NO_TRANSLATION = { x: 0, y: 0 };

/**
 * Determines the elements bounding rect for the provided elements and their measurements.
 *
 * NOTE: This will adjust board like elements to add padding around them and will also get the
 *  precise measurements of line elements by getting their path client rects and translating it
 *  into the canvas document coordinate system.
 *
 * FIXME - isBoardLike should probably be isIconView or similar instead
 */
export const getElementsBoundingRect = (
    elements: Immutable.Map<string, MNElement>,
    measurements: Immutable.Map<string, Rect>,
    gridSize: number,
    canvasScroll?: Point,
    zoomScale?: number,
    zoomTranslation?: Point,
): Rect => {
    let canvasViewportOffset: Point;
    const getCanvasViewportOffset = () => {
        if (canvasViewportOffset) return canvasViewportOffset;

        const canvasViewportEl = document.getElementById(CANVAS_VIEWPORT_ID);

        if (!canvasViewportEl) return NO_TRANSLATION;

        const boundingRect = canvasViewportEl.getBoundingClientRect();

        canvasViewportOffset = {
            x: getX(boundingRect),
            y: getY(boundingRect),
        };

        return canvasViewportOffset;
    };

    const adjustElementMeasurements = (element: MNElement | undefined): Rect | null => {
        const elementId = getElementId(element);
        const measurement = measurements.get(elementId);

        // Boards can be wider than what they report, due to their headings
        if (isBoardLike(element)) return rectLib.addPadding(4 * gridSize, 0, measurement);

        // Lines do not exist where their element bounding rect reports, so we have to find the actual line path
        if (isLine(element)) {
            const linePath = document.querySelector(`#${getDomElementId(elementId)} .line-path`);

            // If we can't find the line in the DOM, don't include its measurement
            if (!linePath) return null;

            const boundingRect = linePath.getBoundingClientRect();

            if (!boundingRect) return null;

            return convertClientRectToUnscaledSectionCoordinates(
                boundingRect,
                getCanvasViewportOffset(),
                canvasScroll,
                zoomScale,
                zoomTranslation,
            );
        }

        return measurement;
    };

    // Get the measurements for each canvas element, adjusting boards and lines to more
    // closely match their shapes
    const elementMeasurements = elements.map(adjustElementMeasurements).filter((measurement) => !!measurement);
    return rectLib.getBoundingRect(elementMeasurements);
};
